import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'

import { applicationsApplicationsAccessListInfiniteOptions } from 'src/api/main'

import { countAccessByApplication } from '../utils/appsBoard'

const PER_PAGE = 200

/**
 * People-with-access count per application, keyed by application id.
 *
 * The access list is org-scoped and filterable, so one paginated sweep covers
 * every app on the board — cheaper than a per-app `count` request each. The
 * grouping is client-side because the API exposes no aggregate endpoint.
 */
export const useWorkspaceAppAccessCounts = (workspaceId: number) => {
  const enabled = Number.isFinite(workspaceId) && workspaceId > 0

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError
  } = useInfiniteQuery({
    ...applicationsApplicationsAccessListInfiniteOptions({
      query: { organization: workspaceId, per_page: PER_PAGE }
    }),
    getNextPageParam: ({ next }) => next ?? undefined,
    enabled
  })

  // Counts are only correct once every page has landed.
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return
    }
    void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const countsByAppId = useMemo(
    () =>
      countAccessByApplication(
        data?.pages?.flatMap((page) => page.results ?? []) ?? []
      ),
    [data?.pages]
  )

  const isIncomplete = isLoading || hasNextPage || isFetchingNextPage

  return {
    countsByAppId,
    /** True until the last page lands — counts before this are partial. */
    isLoading: isIncomplete,
    /** The sweep failed. Callers must render "unknown", never 0. */
    isError,
    /**
     * Whether a count can be trusted for display. Guards against showing a
     * confident `0` when the sweep failed or is still running.
     */
    isCountReliable: !isError && !isIncomplete
  }
}
